import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useConfig } from '../../contexts/ConfigContext';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { toast } from 'react-toastify';
import { User, Lock, Save, ExternalLink, ChevronDown, ChevronUp, Upload, X, Image as ImageIcon, Calendar as CalendarIcon } from 'lucide-react';
import ProfileChoices from './ProfileChoices';
import { formatDateLongRu } from '../../utils/dateUtils';
import './ProfilePage.css';

const ProfilePage = () => {
    const { user, loading: authLoading, updateUser } = useAuth();
    const { config } = useConfig();
    const [activeTab, setActiveTab] = useState('profile');
    const [loading, setLoading] = useState(false);
    const [topicPricesExpanded, setTopicPricesExpanded] = useState(false);
    const [telegramTestLoading, setTelegramTestLoading] = useState(false);

    // Загрузка опций для селектов
    const { data: profileOptions, isLoading: optionsLoading, error: optionsError } = useQuery('profileOptions', async () => {
        const response = await axios.get(`${API_BASE_URL}/config/profile-options`);
        return response.data;
    }, {enabled: !!user?.is_therapist});

    const [profileData, setProfileData] = useState({
        last_name: user?.last_name || '',
        name: user?.name || '',
        phone: user?.phone || '',
        photo_url: user?.photo_url || '',
        description: user?.description || '',
        short_description: user?.short_description || '',
        therapist_aggregator_short: user?.therapist_aggregator_short || '',
        therapist_aggregator_full: user?.therapist_aggregator_full || '',
        therapist_education: user?.therapist_education || '',
        city: user?.city || [],
        language: user?.language || [],
        therapy_topics: user?.therapy_topics || [],
        work_format: user?.work_format || [],
        video_card_url: user?.video_card_url || '',
        price: user?.price || null,
        topic_prices: user?.topic_prices || {},
        qualification: Array.isArray(user?.qualification)
            ? user.qualification
            : (user?.qualification ? [user.qualification] : [])
    });
    const [certificates, setCertificates] = useState(user?.certificates || []);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [uploadingCertificates, setUploadingCertificates] = useState(false);

    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const [vacationStart, setVacationStart] = useState('');
    const [vacationEnd, setVacationEnd] = useState('');

    const { data: myVacations, refetch: refetchMyVacations } = useQuery(
        'myVacations',
        async () => {
            const response = await axios.get(`${API_BASE_URL}/vacations/my`, { withCredentials: true });
            return response.data;
        },
        { enabled: !!user?.is_teacher }
    );

    const therapistProfileLocked = Boolean(user?.is_therapist && user?.therapist_profile_status === 'published');

    useEffect(() => {
        if (user) {
            setProfileData({
                last_name: user.last_name || '',
                name: user.name || '',
                phone: user.phone || '',
                photo_url: user.photo_url || '',
                description: user.description || '',
                short_description: user.short_description || '',
                therapist_aggregator_short: user.therapist_aggregator_short || '',
                therapist_aggregator_full: user.therapist_aggregator_full || '',
                therapist_education: user.therapist_education || '',
                city: user.city || [],
                language: user.language || [],
                therapy_topics: user.therapy_topics || [],
                work_format: user.work_format || [],
                video_card_url: user.video_card_url || '',
                price: user.price || null,
                topic_prices: user.topic_prices || {},
                qualification: Array.isArray(user.qualification)
                    ? user.qualification
                    : (user.qualification ? [user.qualification] : [])
            });
            setCertificates(user.certificates || []);
        }
    }, [user]);

    const handleProfileChange = (e) => {
        const { name, value } = e.target;
        setProfileData(prev => ({ ...prev, [name]: value }));
    };

    const handleMultiSelectChange = (name, selectedOptions) => {
        const values = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
        setProfileData(prev => {
            const newData = { ...prev, [name]: values };
            // При изменении тем-запросов, удаляем цены для удаленных тем
            if (name === 'therapy_topics') {
                const topicPrices = { ...prev.topic_prices };
                Object.keys(topicPrices).forEach(topic => {
                    if (!values.includes(topic)) {
                        delete topicPrices[topic];
                    }
                });
                newData.topic_prices = topicPrices;
            }
            return newData;
        });
    };

    const handleTopicPriceChange = (topic, price) => {
        setProfileData(prev => ({
            ...prev,
            topic_prices: {
                ...prev.topic_prices,
                [topic]: price ? parseFloat(price) : null
            }
        }));
    };

    const removeTopicPrice = (topic) => {
        setProfileData(prev => {
            const newTopicPrices = { ...prev.topic_prices };
            delete newTopicPrices[topic];
            return {
                ...prev,
                topic_prices: newTopicPrices
            };
        });
    };

    // Преобразование массивов в опции для react-select
    const toOptions = (arr) => arr ? arr.map(item => ({ value: item, label: item })) : [];
    const getSelectedOptions = (values) => values ? values.map(v => ({ value: v, label: v })) : [];

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordData(prev => ({ ...prev, [name]: value }));
    };

    const handlePhotoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            toast.error('Выберите изображение');
            return;
        }

        if (file.size > 30 * 1024 * 1024) {
            toast.error('Размер файла не должен превышать 30MB');
            return;
        }

        setUploadingPhoto(true);
        try {
            const formData = new FormData();
            formData.append('photo', file);

            const response = await axios.post(`${API_BASE_URL}/upload/photo`, formData, {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            setProfileData(prev => ({ ...prev, photo_url: response.data.photo_url }));
            toast.success('Фото загружено успешно');
            window.location.reload();
        } catch (error) {
            console.error('Ошибка загрузки фото:', error);
            toast.error(error.response?.data?.message || 'Ошибка загрузки фото');
        } finally {
            setUploadingPhoto(false);
        }
    };

    const handleDeletePhoto = async () => {
        if (!window.confirm('Удалить загруженное фото?')) return;

        try {
            await axios.delete(`${API_BASE_URL}/upload/photo`, { withCredentials: true });
            setProfileData(prev => ({ ...prev, photo_url: '' }));
            toast.success('Фото удалено');
            window.location.reload();
        } catch (error) {
            console.error('Ошибка удаления фото:', error);
            toast.error(error.response?.data?.message || 'Ошибка удаления фото');
        }
    };

    const handleCertificatesUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const invalidFiles = files.filter(file => !file.type.startsWith('image/'));
        if (invalidFiles.length > 0) {
            toast.error('Выберите только изображения');
            return;
        }

        const largeFiles = files.filter(file => file.size > 30 * 1024 * 1024);
        if (largeFiles.length > 0) {
            toast.error('Размер каждого файла не должен превышать 30MB');
            return;
        }

        setUploadingCertificates(true);
        try {
            const formData = new FormData();
            files.forEach(file => formData.append('certificates', file));

            const response = await axios.post(`${API_BASE_URL}/upload/certificates`, formData, {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            setCertificates(response.data.certificates);
            toast.success('Сертификаты загружены успешно');
            window.location.reload();
        } catch (error) {
            console.error('Ошибка загрузки сертификатов:', error);
            toast.error(error.response?.data?.message || 'Ошибка загрузки сертификатов');
        } finally {
            setUploadingCertificates(false);
        }
    };

    const handleDeleteCertificate = async (certificatePath) => {
        if (!window.confirm('Удалить этот сертификат?')) return;

        try {
            const response = await axios.delete(`${API_BASE_URL}/upload/certificate`, {
                withCredentials: true,
                data: { certificatePath }
            });

            setCertificates(response.data.certificates);
            toast.success('Сертификат удален');
        } catch (error) {
            console.error('Ошибка удаления сертификата:', error);
            toast.error(error.response?.data?.message || 'Ошибка удаления сертификата');
        }
    };

    const handleProfileSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            // Очищаем пустые цены перед отправкой
            const cleanedTopicPrices = {};
            Object.keys(profileData.topic_prices || {}).forEach(topic => {
                if (profileData.topic_prices[topic]) {
                    cleanedTopicPrices[topic] = profileData.topic_prices[topic];
                }
            });

            const submitData = {
                ...profileData,
                price: profileData.price || null,
                topic_prices: cleanedTopicPrices
            };

            const {data: savedProfile} = await axios.put(`${API_BASE_URL}/auth/profile`, submitData, {
                withCredentials: true
            });

            toast.success('Профиль обновлен');
            updateUser(savedProfile);
        } catch (error) {
            console.error('Ошибка обновления профиля:', error);
            toast.error(error.response?.data?.message || 'Ошибка обновления профиля');
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();

        if (passwordData.newPassword !== passwordData.confirmPassword) {
            toast.error('Пароли не совпадают');
            return;
        }

        if (passwordData.newPassword.length < 6) {
            toast.error('Пароль должен быть не менее 6 символов');
            return;
        }

        setLoading(true);

        try {
            await axios.put(
                `${API_BASE_URL}/auth/change-password`,
                {
                    currentPassword: passwordData.currentPassword,
                    newPassword: passwordData.newPassword
                },
                { withCredentials: true }
            );

            toast.success('Пароль изменен');
            setPasswordData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
        } catch (error) {
            console.error('Ошибка изменения пароля:', error);
            toast.error(error.response?.data?.message || 'Ошибка изменения пароля');
        } finally {
            setLoading(false);
        }
    };

    if (authLoading) {
        return (
            <div className="loading">
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <div className="profile-page">
            <div className="profile-container">
                <header className="profile-hero"><div className="profile-hero-avatar">{profileData.photo_url ? <img src={profileData.photo_url} alt="Фото профиля"/> : <User size={34}/>}</div><div><p className="profile-kicker">Личный кабинет</p><h1>{[user?.last_name,user?.name].filter(Boolean).join(' ') || 'Мой профиль'}</h1><div className="profile-role-tags">{user?.role==='admin' && <span>Администратор</span>}{user?.is_teacher && <span>Преподаватель</span>}{user?.is_therapist && <span>Терапевт</span>}{!user?.is_teacher && !user?.is_therapist && user?.role!=='admin' && <span>Пользователь</span>}</div></div></header>
                <div className="profile-workspace">
                <nav className="profile-tabs" aria-label="Разделы профиля">
                    {[['profile','Общие данные',User],...(user?.is_teacher?[['teacher','Преподаватель',User],['vacation','Отпуск',CalendarIcon]]:[]),...(user?.is_therapist?[['therapist','Терапевт',User]]:[]),['password','Безопасность',Lock]].map(([key,label,Icon])=><button type="button" key={key} className={activeTab===key?'tab-btn active':'tab-btn'} aria-current={activeTab===key?'page':undefined} onClick={()=>setActiveTab(key)}><Icon size={18}/>{label}</button>)}
                </nav>
                <div className="profile-content">
                    {['profile','teacher','therapist'].includes(activeTab) && (
                        <form onSubmit={handleProfileSubmit} className="profile-form">
                            {activeTab === 'profile' && <section className="profile-section"><div className="profile-section-heading"><h2>Общие данные</h2><p>Фото, имя и контакты используются во всех ваших профилях.</p></div>
                            <div className="form-group">
                                <label>Email</label>
                                <input type="email" value={user?.email || ''} disabled />
                                <small className="form-hint">Email нельзя изменить</small>
                            </div>



                            <div className="form-row profile-name-row">
                                <div className="form-group">
                                    <label>Фамилия</label>
                                    <input
                                        type="text"
                                        name="last_name"
                                        value={profileData.last_name}
                                        onChange={handleProfileChange}
                                        placeholder="Фамилия"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Имя *</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={profileData.name}
                                        onChange={handleProfileChange}
                                        required
                                        placeholder="Имя"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Телефон</label>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={profileData.phone}
                                    onChange={handleProfileChange}
                                    placeholder="+7 (999) 123-45-67"
                                />
                            </div>

                            <div className="form-group">
                                <label>Telegram</label>
                                {user?.telegram_chat_id ? (
                                    <>
                                        <p className="form-hint">
                                            Telegram-уведомления подключены
                                            {user?.telegram_username ? ` (${user.telegram_username})` : ''}.
                                        </p>
                                        <div className="telegram-buttons-row">
                                            <button
                                                type="button"
                                                className="btn btn-secondary"
                                                onClick={async () => {
                                                    try {
                                                        await axios.post(
                                                            `${API_BASE_URL}/auth/telegram-unlink`,
                                                            {},
                                                            { withCredentials: true }
                                                        );
                                                        toast.success('Telegram отвязан. Обновите страницу.');
                                                    } catch (error) {
                                                        const msg = error.response?.data?.message || 'Ошибка отвязки Telegram';
                                                        toast.error(msg);
                                                    }
                                                }}
                                            >
                                                Отвязать Telegram
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-secondary"
                                                disabled={telegramTestLoading}
                                                onClick={async () => {
                                                    try {
                                                        setTelegramTestLoading(true);
                                                        await axios.post(
                                                            `${API_BASE_URL}/auth/telegram-test`,
                                                            {},
                                                            { withCredentials: true }
                                                        );
                                                        toast.success('Тестовое сообщение отправлено (проверьте Telegram)');
                                                    } catch (error) {
                                                        const msg = error.response?.data?.message || 'Ошибка отправки тестового сообщения';
                                                        toast.error(msg);
                                                    } finally {
                                                        setTelegramTestLoading(false);
                                                    }
                                                }}
                                            >
                                                {telegramTestLoading ? 'Отправка...' : 'Отправить тестовое сообщение'}
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="telegram-field-row">
                                            <a
                                                href={config?.telegramBotUrl || 'https://t.me/calendar_mag_bot'}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="btn btn-secondary"
                                                style={{ whiteSpace: 'nowrap' }}
                                            >
                                                Привязать Telegram
                                            </a>
                                        </div>
                                        <small className="form-hint">
                                            Нажмите «Привязать Telegram», откроется бот @{config?.telegramBotUsername || 'calendar_mag_bot'}.<br />
                                            В боте отправьте ваш email из календаря, после чего уведомления (заявки, вопросы, записи)
                                            будут дублироваться в Telegram.
                                        </small>
                                    </>
                                )}
                            </div>

                            <div className="form-group">
                                <label>Фото профиля</label>
                                <div className="photo-upload-section">
                                    <div className="photo-upload-controls">
                                        <label className="btn-upload-photo">
                                            <Upload size={18} />
                                            {uploadingPhoto ? 'Загрузка...' : 'Загрузить фото'}
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handlePhotoUpload}
                                                style={{ display: 'none' }}
                                                disabled={uploadingPhoto}
                                            />
                                        </label>
                                        {profileData.photo_url && profileData.photo_url.startsWith('/uploads/photos/') && (
                                            <button
                                                type="button"
                                                className="btn-delete-photo"
                                                onClick={handleDeletePhoto}
                                            >
                                                <X size={18} />
                                                Удалить
                                            </button>
                                        )}
                                    </div>
                                    {profileData.photo_url && (
                                        <div className="photo-preview">
                                            <img
                                                src={profileData.photo_url.startsWith('/uploads/')
                                                    ? `${window.location.origin}${profileData.photo_url}`
                                                    : profileData.photo_url}
                                                alt="Превью"
                                            />
                                        </div>
                                    )}
                                    <div className="photo-url-input">
                                        <label>Или укажите ссылку на фото</label>
                                        <input
                                            type="text"
                                            name="photo_url"
                                            value={profileData.photo_url}
                                            onChange={handleProfileChange}
                                            placeholder="https://example.com/photo.jpg"
                                        />
                                    </div>
                                </div>
                            </div>

                            </section>}
                            {user?.is_teacher && activeTab === 'teacher' && (
                                <section className="profile-section"><div className="profile-section-heading"><h2>Профиль преподавателя</h2><p>Расскажите об опыте преподавания и профессиональных интересах.</p></div>
                                    <div className="form-group">
                                        <label>Краткое описание (преподаватель)</label>
                                        <textarea
                                            name="short_description"
                                            value={profileData.short_description}
                                            onChange={handleProfileChange}
                                            rows="2"
                                            maxLength="500"
                                            placeholder="Краткая информация для страницы преподавателя"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Полное описание (преподаватель)</label>
                                        <textarea
                                            name="description"
                                            value={profileData.description}
                                            onChange={handleProfileChange}
                                            rows="5"
                                            placeholder="Подробная информация для страницы преподавателя"
                                        />
                                    </div>
                                </section>
                            )}

                            {/* Поля для психологов */}
                            {activeTab === 'therapist' && user?.is_therapist && optionsLoading && <p>Загрузка настроек профиля…</p>}
                            {activeTab === 'therapist' && optionsError && <p role="alert">Не удалось загрузить настройки профиля. Обновите страницу.</p>}
                            {user?.is_therapist && activeTab === 'therapist' && profileOptions && (
                                <>
                                    <div className="therapist-fields-divider">
                                        <h2>Профиль терапевта</h2><p>Информация, которую увидят клиенты при выборе специалиста.</p>{therapistProfileLocked && <p className="therapist-profile-locked-notice">Профиль опубликован. Для изменения профессиональных данных обратитесь к администратору. Общие данные можно редактировать самостоятельно.</p>}
                                    </div>

                                    <div className="form-group">
                                        <label>Краткое описание</label>
                                        <textarea
                                            name="therapist_aggregator_short"
                                            value={profileData.therapist_aggregator_short}
                                            onChange={handleProfileChange}
                                            rows="2"
                                            maxLength="600"
                                            placeholder="Краткий текст для карточки в списке психологов"
                                            disabled={therapistProfileLocked}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Полное описание</label>
                                        <textarea
                                            name="therapist_aggregator_full"
                                            value={profileData.therapist_aggregator_full}
                                            onChange={handleProfileChange}
                                            rows="5"
                                            placeholder="Подробный текст для блока «О себе» на публичной странице"
                                            disabled={therapistProfileLocked}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Образование</label>
                                        <textarea
                                            name="therapist_education"
                                            value={profileData.therapist_education}
                                            onChange={handleProfileChange}
                                            rows="4"
                                            placeholder="Образование, курсы повышения квалификации (под полным описанием на публичной странице)"
                                            disabled={therapistProfileLocked}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Города</label>
                                        <ProfileChoices
                                            isMulti
                                            isDisabled={therapistProfileLocked}
                                            options={toOptions(profileOptions.cities)}
                                            value={getSelectedOptions(profileData.city)}
                                            onChange={(selected) => handleMultiSelectChange('city', selected)}
                                            placeholder="Выберите города..."
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Языки</label>
                                        <ProfileChoices
                                            isMulti
                                            isDisabled={therapistProfileLocked}
                                            options={toOptions(profileOptions.languages)}
                                            value={getSelectedOptions(profileData.language)}
                                            onChange={(selected) => handleMultiSelectChange('language', selected)}
                                            placeholder="Выберите языки..."
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Темы-запросы</label>
                                        <ProfileChoices
                                            isMulti
                                            isDisabled={therapistProfileLocked}
                                            options={toOptions(profileOptions.therapyTopics)}
                                            value={getSelectedOptions(profileData.therapy_topics)}
                                            onChange={(selected) => handleMultiSelectChange('therapy_topics', selected)}
                                            placeholder="Выберите темы..."
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Форматы работы</label>
                                        <ProfileChoices
                                            isMulti
                                            isDisabled={therapistProfileLocked}
                                            options={toOptions(profileOptions.formats)}
                                            value={getSelectedOptions(profileData.work_format)}
                                            onChange={(selected) => handleMultiSelectChange('work_format', selected)}
                                            placeholder="Выберите форматы..."
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Квалификации</label>
                                        <ProfileChoices
                                            isMulti
                                            isDisabled={therapistProfileLocked}
                                            options={toOptions(profileOptions.qualifications || [])}
                                            value={getSelectedOptions(profileData.qualification)}
                                            onChange={(selected) => handleMultiSelectChange('qualification', selected)}
                                            placeholder="Выберите квалификации..."
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Базовая стоимость консультации (руб.)</label>
                                        <input
                                            type="number"
                                            name="price"
                                            value={profileData.price || ''}
                                            onChange={handleProfileChange}
                                            placeholder="0"
                                            min="0"
                                            step="100"
                                            disabled={therapistProfileLocked}
                                        />
                                        <small className="form-hint">Базовая цена, если не указана цена для конкретной темы</small>
                                    </div>

                                    {/* Цены по темам-запросам */}
                                    {profileData.therapy_topics && profileData.therapy_topics.length > 0 && (
                                        <div className="form-group topic-prices-section">
                                            <button
                                                type="button"
                                                className="topic-prices-header"
                                                onClick={() => setTopicPricesExpanded(!topicPricesExpanded)}
                                                disabled={therapistProfileLocked}
                                            >
                                                <div className="topic-prices-header-content">
                                                    <span>Цены по темам-запросам</span>
                                                    <span className="topic-prices-count">
                                                        ({Object.keys(profileData.topic_prices || {}).filter(t => profileData.topic_prices[t]).length} из {profileData.therapy_topics.length})
                                                    </span>
                                                </div>
                                                {topicPricesExpanded ? (
                                                    <ChevronUp size={20} />
                                                ) : (
                                                    <ChevronDown size={20} />
                                                )}
                                            </button>

                                            <div className={`topic-prices-content ${topicPricesExpanded ? 'expanded' : 'collapsed'}`}>
                                                <div className="topic-prices-list">
                                                    {profileData.therapy_topics.map(topic => {
                                                        const topicPrice = profileData.topic_prices?.[topic] || '';
                                                        return (
                                                            <div key={topic} className="topic-price-item">
                                                                <label className="topic-price-label">{topic}</label>
                                                                <div className="topic-price-input-group">
                                                                    <input
                                                                        type="number"
                                                                        value={topicPrice}
                                                                        onChange={(e) => handleTopicPriceChange(topic, e.target.value)}
                                                                        placeholder="Базовая цена"
                                                                        min="0"
                                                                        step="100"
                                                                        className="topic-price-input"
                                                                        disabled={therapistProfileLocked}
                                                                    />
                                                                    <span className="topic-price-currency">₽</span>
                                                                    {topicPrice && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => removeTopicPrice(topic)}
                                                                            className="btn-remove-price"
                                                                            title="Удалить цену"
                                                                            disabled={therapistProfileLocked}
                                                                        >
                                                                            ×
                                                                        </button>
                                                                    )}
                                                                </div>
                                                                {!topicPrice && (
                                                                    <small className="topic-price-hint">
                                                                        Будет использована базовая цена: {profileData.price || 'не указана'} ₽
                                                                    </small>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                                <small className="form-hint">
                                                    Укажите отдельную цену для каждой темы. Если цена не указана, будет использована базовая цена.
                                                </small>
                                            </div>
                                        </div>
                                    )}

                                    <div className="form-group">
                                        <label>Ссылка на видеовизитку</label>
                                        <input
                                            type="url"
                                            name="video_card_url"
                                            value={profileData.video_card_url}
                                            onChange={handleProfileChange}
                                            placeholder="https://youtube.com/watch?v=..."
                                            disabled={therapistProfileLocked}
                                        />
                                    </div>

                                    <div className="form-group certificates-section">
                                        <label>Сертификаты</label>
                                        <div className="certificates-upload-section">
                                            <label className="btn-upload-certificates">
                                                <ImageIcon size={18} />
                                                {uploadingCertificates ? 'Загрузка...' : 'Загрузить сертификаты'}
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    multiple
                                                    onChange={handleCertificatesUpload}
                                                    style={{ display: 'none' }}
                                                    disabled={uploadingCertificates || therapistProfileLocked}
                                                />
                                            </label>
                                            <small className="form-hint">Можно загрузить несколько файлов одновременно (до 20 штук, каждый до 30MB)</small>

                                            {certificates && certificates.length > 0 && (
                                                <div className="certificates-grid">
                                                    {certificates.map((cert, index) => (
                                                        <div key={index} className="certificate-item">
                                                            <img
                                                                src={cert.startsWith('/uploads/')
                                                                    ? `${window.location.origin}${cert}`
                                                                    : cert}
                                                                alt={`Сертификат ${index + 1}`}
                                                            />
                                                            <button
                                                                type="button"
                                                                className="btn-delete-certificate"
                                                                onClick={() => handleDeleteCertificate(cert)}
                                                                title="Удалить сертификат"
                                                                disabled={therapistProfileLocked}
                                                            >
                                                                <X size={16} />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}

                            <div className="form-actions">
                                <button type="submit" className="btn btn-primary" disabled={loading || (activeTab === 'therapist' && therapistProfileLocked)}>
                                    <Save size={18} />
                                    {loading ? 'Сохранение...' : 'Сохранить изменения'}
                                </button>
                                {user?.is_therapist && (
                                    <Link
                                        to={config?.code === 'mag' ? `${config.agregatorUrl || 'https://test.mag-gestalt.com'}/specialists/${user?.id}` : `/public/profile/${user?.id}`}
                                        className="btn btn-secondary btn-public-link"
                                        target="_blank"
                                    >
                                        <ExternalLink size={18} />
                                        Профиль терапевта
                                    </Link>
                                )}

                                {user?.is_teacher && (
                                    <Link
                                        to={`/public/teacher/${user?.id}`}
                                        className="btn btn-secondary btn-public-link"
                                        target="_blank"
                                    >
                                        <ExternalLink size={18} />
                                        Профиль преподавателя
                                    </Link>
                                )}
                            </div>
                        </form>
                    )}

                    {activeTab === 'password' && (
                        <form onSubmit={handlePasswordSubmit} className="profile-form"><div className="profile-section-heading"><h2>Безопасность</h2><p>Для смены пароля укажите текущий и задайте новый.</p></div>
                            <div className="form-group">
                                <label>Текущий пароль *</label>
                                <input
                                    type="password"
                                    name="currentPassword"
                                    value={passwordData.currentPassword}
                                    onChange={handlePasswordChange}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>Новый пароль *</label>
                                <input
                                    type="password"
                                    name="newPassword"
                                    value={passwordData.newPassword}
                                    onChange={handlePasswordChange}
                                    required
                                    minLength="6"
                                />
                                <small className="form-hint">Минимум 6 символов</small>
                            </div>

                            <div className="form-group">
                                <label>Подтвердите новый пароль *</label>
                                <input
                                    type="password"
                                    name="confirmPassword"
                                    value={passwordData.confirmPassword}
                                    onChange={handlePasswordChange}
                                    required
                                    minLength="6"
                                />
                            </div>

                            <button type="submit" className="btn btn-primary" disabled={loading}>
                                <Lock size={18} />
                                {loading ? 'Изменение...' : 'Изменить пароль'}
                            </button>
                        </form>
                    )}

                    {activeTab === 'vacation' && user?.is_teacher && (
                        <div className="profile-form">
                            <div className="form-section">
                                <h2>Отпуск</h2>
                                <p className="form-hint">
                                    Отпуск учитывается при назначении преподавателя в расписании и отображается в календаре.
                                </p>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Дата начала</label>
                                        <input type="date" value={vacationStart} onChange={(e) => setVacationStart(e.target.value)} />
                                    </div>
                                    <div className="form-group">
                                        <label>Дата окончания</label>
                                        <input type="date" value={vacationEnd} onChange={(e) => setVacationEnd(e.target.value)} />
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    style={{ marginTop: '10px' }}
                                    disabled={!vacationStart || !vacationEnd}
                                    onClick={async () => {
                                        try {
                                            await axios.post(
                                                `${API_BASE_URL}/vacations/my`,
                                                { start_date: vacationStart, end_date: vacationEnd },
                                                { withCredentials: true }
                                            );
                                            toast.success('Отпуск добавлен');
                                            setVacationStart('');
                                            setVacationEnd('');
                                            refetchMyVacations();
                                        } catch (error) {
                                            const msg = error.response?.data?.message || 'Ошибка добавления отпуска';
                                            toast.error(msg);
                                        }
                                    }}
                                >
                                    Добавить отпуск
                                </button>
                            </div>

                            <div className="form-section">
                                <h3 style={{ marginTop: '20px' }}>Мои отпуска</h3>
                                {(!myVacations || myVacations.length === 0) ? (
                                    <p className="form-hint">Отпусков пока нет</p>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        {myVacations.map(v => (
                                            <div
                                                key={v.id}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    padding: '10px',
                                                    border: '1px solid #e0e0e0',
                                                    borderRadius: '8px'
                                                }}
                                            >
                                                <div>
                                                    <strong>{formatDateLongRu(v.start_date)} - {formatDateLongRu(v.end_date)}</strong>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="btn btn-danger"
                                                    onClick={async () => {
                                                        if (!window.confirm('Удалить этот отпуск?')) return;
                                                        try {
                                                            await axios.delete(`${API_BASE_URL}/vacations/my/${v.id}`, { withCredentials: true });
                                                            toast.success('Отпуск удалён');
                                                            refetchMyVacations();
                                                        } catch (error) {
                                                            const msg = error.response?.data?.message || 'Ошибка удаления отпуска';
                                                            toast.error(msg);
                                                        }
                                                    }}
                                                >
                                                    Удалить
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            </div>
        </div>
    );
};

export default ProfilePage;

